import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertTriangle, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { useBilling } from "../context/BillingContext";

/**
 * App-shell banner that:
 *   • Trial  — shows "N days left in your free trial" in gold with "Add plan" CTA
 *   • Locked — shows "Your trial has ended — upgrade to unlock" in red with "Upgrade" CTA
 *   • Past due — shows "Your last payment failed — update card" with "Fix billing" CTA
 *   • Active — hidden
 *
 * Hidden on /billing itself (no point nagging on the page that fixes it).
 */
export default function BillingBanner() {
  const { billing } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();

  if (!billing) return null;
  if (location.pathname.startsWith("/billing")) return null;
  if (location.pathname.startsWith("/invite")) return null;

  const { status, locked, trial_days_left, plan_name } = billing;

  // Active + unlocked — nothing to say.
  if (status === "active" && !locked) return null;

  let tone = "trial";       // info / amber
  let icon = <Clock className="w-4 h-4" />;
  let text = "";
  let cta  = "Add plan";

  if (locked && status === "past_due") {
    tone = "danger";
    icon = <AlertTriangle className="w-4 h-4" />;
    text = `Your last ${plan_name || "subscription"} payment failed. Update your card to keep working.`;
    cta  = "Fix billing";
  } else if (locked) {
    tone = "danger";
    icon = <AlertTriangle className="w-4 h-4" />;
    text = status === "canceled"
      ? "Your subscription was cancelled. Pick a plan to unlock your shop."
      : "Your 14-day free trial has ended. Pick a plan to keep making changes.";
    cta  = "Upgrade now";
  } else if (status === "trialing") {
    const d = trial_days_left ?? 0;
    tone = d <= 3 ? "warn" : "trial";
    icon = <Clock className="w-4 h-4" />;
    text = d <= 0
      ? "Your free trial ends today. Add a plan to keep working after tonight."
      : d === 1
        ? "Last day of your free trial — add a plan to keep making changes tomorrow."
        : `${d} days left in your free trial. Add a plan any time — no interruption to your work.`;
    cta  = "Add plan";
  } else {
    return null;
  }

  const palette = {
    trial:  { bg: "bg-amber-500/8 border-amber-500/25 text-amber-100", pill: "bg-amber-400/15 text-amber-200" },
    warn:   { bg: "bg-orange-500/10 border-orange-500/30 text-orange-100", pill: "bg-orange-400/20 text-orange-100" },
    danger: { bg: "bg-red-500/10 border-red-500/30 text-red-100", pill: "bg-red-400/20 text-red-100" },
  }[tone];

  return (
    <div
      data-testid="billing-banner"
      className={`flex items-center justify-between gap-4 px-4 py-2 text-xs border-b ${palette.bg}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center shrink-0 ${palette.pill}`}>
          {icon}
        </span>
        <span className="truncate">{text}</span>
      </div>
      <button
        data-testid="billing-banner-cta"
        onClick={() => navigate("/billing")}
        className="flex items-center gap-1 bg-white text-black font-semibold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full hover:bg-slate-100 transition shrink-0"
      >
        {cta} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
